/* src/Pages/SlotsPage/components/SlotTable.tsx */
import React, {
  useState,
  useEffect,
  useMemo,
  useCallback,
  useRef,
} from "react";
import {
  Table,
  Input,
  Button,
  Tag,
  message,
  Select,
  DatePicker,
  Space,
} from "antd";
import type { ColumnsType } from "antd/es/table";
import type { Key } from "react";
import SlotRankModal from "./SlotRankModal";
import dayjs from "dayjs";

import { Slot, PaginationInfo } from "../../../Modules/Slot/Model/Slot";
import { useAppSelector } from "../../../Modules/Presentation/hooks";
import { selectAccounts } from "../../../Modules/Account/Presentation/selectors";
import { selectUser } from "../../../Modules/User/Presentation/selectors";
import { isSlotOperationRestricted } from "../../../Utils/timeRestriction";
import { getSlotPermissions } from "../../../Utils/slotPermissions";

interface Props {
  data: Slot[];
  loading: boolean;
  selected: Key[];
  onSelectChange: (ids: Key[]) => void;
  onInlineUpdate: (id: number, dto: Partial<Slot>) => void;
  onClone?: (slot: Slot) => void;
  localFindQuery?: string;
  localFindMatchIndex?: number;
  onLocalFindMatchCountChange?: (count: number) => void;
  pagination?: PaginationInfo;
  onPageChange?: (page: number, pageSize?: number) => void;
}

/* 상태별 태그 색 */
const colorMap: Record<Slot["status"], string> = {
  정상: "green",
  연장: "green",
  환불: "red",
  종료: "orange",
};

function getRankCheckStatus(slot: Slot) {
  if (slot.rank_check_status) return slot.rank_check_status;
  return slot.rank_calculating ? "running" : undefined;
}

/** 키워드·URL이 모두 있어야 순위 조회 대상 */
function hasRankTarget(
  slot: Pick<Slot, "main_keyword" | "product_url" | "compare_url">,
) {
  const keyword = String(slot.main_keyword ?? "").trim();
  const productUrl = String(slot.product_url ?? "").trim();
  const compareUrl = String(slot.compare_url ?? "").trim();
  return Boolean(keyword && (productUrl || compareUrl));
}

function formatRankLabel(rank: number | null | undefined): string {
  if (rank === null || rank === undefined || rank > 500) return "500위 밖";
  return `${rank}위`;
}

/**
 * 오늘 조회 완료(complete)면 today_rank + new
 * 미완료이고 yesterday_rank가 있으면 어제 순위 표시
 * 둘 다 없으면 오늘 상태(대기/조회/실패) 표시
 * 대기/실패 상태에서도 이전 순위 이력을 확인할 수 있음
 */
function getRankCellView(slot: Slot): {
  label: string;
  showNew: boolean;
  color?: string;
  clickable: boolean;
} {
  if (!hasRankTarget(slot)) {
    return {
      label: "순위 없음",
      showNew: false,
      color: "#999",
      clickable: false,
    };
  }

  const status = getRankCheckStatus(slot);

  if (status === "complete") {
    return {
      label: formatRankLabel(slot.today_rank),
      showNew: true,
      color:
        formatRankLabel(slot.today_rank) === "500위 밖" ? "#999" : undefined,
      clickable: true,
    };
  }

  if (typeof slot.yesterday_rank === "number") {
    return {
      label: formatRankLabel(slot.yesterday_rank),
      showNew: false,
      color:
        formatRankLabel(slot.yesterday_rank) === "500위 밖"
          ? "#999"
          : undefined,
      clickable: true,
    };
  }

  if (status === "running") {
    return {
      label: "조회 중",
      showNew: false,
      color: "#1677ff",
      clickable: false,
    };
  }
  if (status === "failed") {
    return {
      label: "확인 실패",
      showNew: false,
      color: "#ff4d4f",
      clickable: true,
    };
  }

  if (!slot.yesterday_rank && !slot.today_rank) {
    return { label: "순위 밖", showNew: false, color: "#999", clickable: true };
  }

  // idle / pending / 상태 없음
  return { label: "대기 중", showNew: false, color: "#999", clickable: true };
}

function RankCellContent({
  label,
  showNew,
  color,
  cursor,
  onClick,
  renderLocalFindText,
}: {
  label: string;
  showNew: boolean;
  color?: string;
  cursor?: string;
  onClick?: () => void;
  renderLocalFindText: (
    value: string | number | null | undefined,
  ) => React.ReactNode;
}) {
  return (
    <span
      style={{
        color,
        cursor: cursor ?? (onClick ? "pointer" : "default"),
        display: "inline-flex",
        alignItems: "flex-start",
        justifyContent: "center",
        gap: 2,
        whiteSpace: "nowrap",
        lineHeight: 1.2,
      }}
      onClick={onClick}
    >
      {renderLocalFindText(label)}
      {showNew && (
        <span
          style={{
            fontSize: 8,
            fontWeight: 700,
            lineHeight: 1,
            color: "#cf1322",
            marginTop: 0,
            letterSpacing: 0.1,
            textTransform: "uppercase",
            transform: "translateY(-1px)",
          }}
        >
          new
        </span>
      )}
    </span>
  );
}

/* 빈값이면 자동 편집을 유발하는 필드
 * mid_single은 생성 후 서버가 비동기로 채우므로 제외 (대기 중 수정 모드 자동 오픈 방지) */
const blankCheckFields: (keyof Slot)[] = ["main_keyword", "product_url"];

const dateFormat = "YYYY-MM-DD";
const VIRTUAL_ROW_THRESHOLD = 120;
const VIRTUAL_SCROLL_Y = 620;
const VIRTUAL_SCROLL_X = 2200;
const FALLBACK_ROW_HEIGHT = 54;
const escapeRegExp = (value: string) =>
  value.replace(/[.*+?^${}()|[\]\\]/g, "\\$&");

type TableMatch = {
  slotId: number;
  rowIndex: number;
};

type TableScroller = {
  scrollTo?: (config: { index?: number; key?: Key; top?: number }) => void;
  nativeElement?: HTMLDivElement | null;
};

/** 테이블 셀 텍스트 입력: 로컬 상태로 타이핑하고 drafts는 ref만 갱신해 전체 리렌더를 막음 */
const SlotDraftTextInput = React.memo(function SlotDraftTextInput({
  slotId,
  field,
  initialValue,
  onDraftChange,
  validate,
}: {
  slotId: number;
  field: keyof Slot;
  initialValue: string;
  onDraftChange: (slotId: number, patch: Partial<Slot>) => void;
  validate?: (value: string) => boolean;
}) {
  const [value, setValue] = useState(initialValue);

  useEffect(() => {
    setValue(initialValue);
  }, [initialValue, slotId, field]);

  const status =
    validate && value && !validate(value) ? ("error" as const) : undefined;

  return (
    <Input
      value={value}
      status={status}
      onChange={(e) => {
        const next = e.target.value;
        setValue(next);
        onDraftChange(slotId, { [field]: next } as Partial<Slot>);
      }}
    />
  );
});

const SlotTable: React.FC<Props> = ({
  data,
  loading,
  selected,
  onSelectChange,
  onInlineUpdate,
  onClone,
  localFindQuery = "",
  localFindMatchIndex = -1,
  onLocalFindMatchCountChange,
  pagination,
  onPageChange,
}) => {
  const accounts = useAppSelector(selectAccounts);
  const currentUser = useAppSelector(selectUser);
  const isAdmin = currentUser?.grade === "ADMIN";
  const canEditDates = isAdmin && !currentUser?.is_limited_partner_admin;

  // 시간 제한 확인
  const isRestricted = currentUser
    ? isSlotOperationRestricted(currentUser, "수정")
    : false;

  const sellers = useMemo(
    () =>
      accounts
        .filter((a) => a.grade === "SELLER")
        .sort((a, b) => a.username.localeCompare(b.username)),
    [accounts],
  );

  // accounts.find()를 셀마다 반복하지 않도록 id->username 맵 캐싱
  const usernameById = useMemo(() => {
    const m = new Map<number, string>();
    for (const a of accounts) m.set(a.id, a.username);
    return m;
  }, [accounts]);

  const getUsername = useCallback(
    (id: number | null | undefined) => {
      if (!id) return "-";
      if (currentUser && id === currentUser.id) return currentUser.username;
      return usernameById.get(id) ?? "-";
    },
    [currentUser, usernameById],
  );

  /* URL 검사 */
  const isValidUrl = useCallback((url?: string) => {
    if (!url) return false;
    try {
      new URL(url);
      return true;
    } catch {
      return false;
    }
  }, []);

  /* 편집 상태 관리 */
  const [editingRows, setEditingRows] = useState<Set<number>>(new Set());
  const [completedRows, setCompletedRows] = useState<Set<number>>(new Set());
  const [savingRows, setSavingRows] = useState<Set<number>>(new Set());
  // 텍스트 입력도 이 ref에 보관한다. 렌더 시 state로 덮으면 미저장 입력이 사라진다.
  const draftsRef = useRef<Record<number, Partial<Slot>>>({});
  const [, setDraftRevision] = useState(0);

  /* 순위 모달 상태 */
  const [rankModalVisible, setRankModalVisible] = useState(false);
  const [rankModalSlotId, setRankModalSlotId] = useState<number | null>(null);
  const [rankModalMainKeyword, setRankModalMainKeyword] = useState<
    string | null
  >(null);

  // 모바일 판별 (<=489px) - 테이블이 화면을 넘지 않도록 처리
  const [isMobile, setIsMobile] = useState(false);
  useEffect(() => {
    const onResize = () => setIsMobile(window.innerWidth <= 489);
    onResize();
    window.addEventListener("resize", onResize);
    return () => window.removeEventListener("resize", onResize);
  }, []);

  const tableRef = useRef<TableScroller | null>(null);
  const tableWrapperRef = useRef<HTMLDivElement>(null);
  const useVirtual = !isMobile && data.length >= VIRTUAL_ROW_THRESHOLD;

  /* ───── 빈칸 자동 편집 트리거 ───── */
  useEffect(() => {
    const idsNeedingEdit = data
      .filter(
        (s) =>
          !s.is_optimistic &&
          s.status !== "환불" &&
          s.status !== "종료" &&
          blankCheckFields.some((f) => {
            const v = s[f];
            return (
              v === undefined ||
              v === null ||
              (typeof v === "string" && v.trim() === "")
            );
          }),
      )
      .map((s) => s.slotId);

    const blockedIds = data.filter((s) => s.is_optimistic).map((s) => s.slotId);

    setEditingRows((prev) => {
      const next = new Set(prev);
      idsNeedingEdit.forEach((id) => {
        if (!completedRows.has(id)) next.add(id);
      });
      blockedIds.forEach((id) => next.delete(id));
      return next;
    });
  }, [data, completedRows]);

  /* 편집 가능 여부 */
  const isEditing = useCallback(
    (r: Slot) => {
      if (r.is_optimistic) return false;
      const permissions = getSlotPermissions(r, currentUser, isRestricted);
      return permissions.canEdit && editingRows.has(r.slotId);
    },
    [currentUser, isRestricted, editingRows],
  );

  /* 현재 값(드래프트 우선) */
  const getVal = useCallback(
    <K extends keyof Slot>(row: Slot, key: K): Slot[K] | "" =>
      draftsRef.current[row.slotId]?.[key] ?? row[key] ?? "",
    [],
  );

  /** Select/DatePicker 등 UI 반영이 필요한 경우만 setState */
  const updateDraft = useCallback((slotId: number, patch: Partial<Slot>) => {
    draftsRef.current = {
      ...draftsRef.current,
      [slotId]: { ...draftsRef.current[slotId], ...patch },
    };
    setDraftRevision((revision) => revision + 1);
  }, []);

  /** 텍스트 입력: ref만 갱신 → 테이블 전체 리렌더 없음 */
  const updateDraftSilent = useCallback(
    (slotId: number, patch: Partial<Slot>) => {
      draftsRef.current = {
        ...draftsRef.current,
        [slotId]: { ...draftsRef.current[slotId], ...patch },
      };
    },
    [],
  );

  const normalizedLocalFindQuery = useMemo(
    () => localFindQuery.trim().toLowerCase(),
    [localFindQuery],
  );
  const localFindKeyword = useMemo(
    () => localFindQuery.trim(),
    [localFindQuery],
  );

  const renderLocalFindText = useCallback(
    (value: string | number | null | undefined) => {
      if (value === null || value === undefined) return value ?? "-";

      const text = String(value);
      if (!localFindKeyword || !normalizedLocalFindQuery || !text) return text;

      const parts = text.split(
        new RegExp(`(${escapeRegExp(localFindKeyword)})`, "ig"),
      );
      if (parts.length === 1) return text;

      return parts.map((part, index) =>
        part.toLowerCase() === normalizedLocalFindQuery ? (
          <mark key={`${text}-${index}`} className="slot-local-find-match">
            {part}
          </mark>
        ) : (
          <React.Fragment key={`${text}-${index}`}>{part}</React.Fragment>
        ),
      );
    },
    [localFindKeyword, normalizedLocalFindQuery],
  );

  const getLocalFindSearchText = useCallback(
    (row: Slot) => {
      const creatorId = row.manager ?? row.creator;
      const creatorDisplay = creatorId
        ? creatorId === 13
          ? "adpa99"
          : row.manager
            ? getUsername(creatorId)
            : (row.creator_username ?? getUsername(creatorId))
        : "-";
      const userDisplay = row.user_username ?? getUsername(row.user);
      const statusDisplay = row.status === "연장" ? "정상" : row.status;
      const rankDisplay = getRankCellView(row).label;

      return [
        row.slotId,
        row.slot_number,
        statusDisplay,
        row.status,
        creatorDisplay,
        row.creator,
        row.creator_username,
        row.manager,
        userDisplay,
        row.user,
        row.user_username,
        row.main_keyword,
        row.mid_single,
        row.mid_compare,
        row.product_url,
        row.compare_url,
        row.memo,
        row.start_date,
        row.end_date,
        row.remaining_days,
        rankDisplay,
      ]
        .filter(
          (value) =>
            value !== undefined && value !== null && `${value}`.trim() !== "",
        )
        .join(" ")
        .toLowerCase();
    },
    [getUsername],
  );

  const localFindMatches = useMemo<TableMatch[]>(() => {
    if (!normalizedLocalFindQuery) return [];

    return data.reduce<TableMatch[]>((acc, row, rowIndex) => {
      if (getLocalFindSearchText(row).includes(normalizedLocalFindQuery)) {
        acc.push({ slotId: row.slotId, rowIndex });
      }
      return acc;
    }, []);
  }, [data, getLocalFindSearchText, normalizedLocalFindQuery]);

  useEffect(() => {
    onLocalFindMatchCountChange?.(localFindMatches.length);
  }, [localFindMatches.length, onLocalFindMatchCountChange]);

  const activeLocalFindMatch = useMemo(() => {
    if (!localFindMatches.length) return null;

    if (
      localFindMatchIndex < 0 ||
      localFindMatchIndex >= localFindMatches.length
    ) {
      return localFindMatches[0];
    }

    return localFindMatches[localFindMatchIndex];
  }, [localFindMatchIndex, localFindMatches]);

  const activeLocalFindSlotId = activeLocalFindMatch?.slotId;

  const scrollToLocalFindMatch = useCallback((match: TableMatch) => {
    const tableApi = tableRef.current;

    if (tableApi?.scrollTo) {
      tableApi.scrollTo({ key: match.slotId });
      return;
    }

    const holder = tableWrapperRef.current?.querySelector(
      ".ant-table-tbody-virtual-holder, .ant-table-body",
    ) as HTMLElement | null;

    if (!holder) return;

    const firstRenderedRow = tableWrapperRef.current?.querySelector(
      "tr.ant-table-row",
    ) as HTMLElement | null;
    const rowHeight =
      firstRenderedRow?.getBoundingClientRect().height || FALLBACK_ROW_HEIGHT;

    holder.scrollTop = match.rowIndex * rowHeight;
  }, []);

  useEffect(() => {
    if (!activeLocalFindMatch || !normalizedLocalFindQuery) return;

    scrollToLocalFindMatch(activeLocalFindMatch);

    const timer = window.setTimeout(
      () => {
        const row = tableWrapperRef.current?.querySelector(
          `tr[data-row-key="${activeLocalFindMatch.slotId}"]`,
        ) as HTMLElement | null;

        if (!row) return;

        // tr에 focus를 주면 상단 "현재 페이지에서 찾기" 입력 포커스가 풀림 — 스크롤만 수행
        row.scrollIntoView({ block: "nearest", inline: "nearest" });
      },
      useVirtual ? 80 : 0,
    );

    return () => window.clearTimeout(timer);
  }, [
    activeLocalFindMatch,
    normalizedLocalFindQuery,
    scrollToLocalFindMatch,
    useVirtual,
  ]);

  /* ───── 컬럼 정의 ───── */
  const columns: ColumnsType<Slot> = useMemo(
    () => [
      {
        title: "슬롯 번호",
        dataIndex: "slotId",
        width: 85,
        align: "center",
        render: (v: number) => renderLocalFindText(v),
      },

      {
        title: "상태",
        dataIndex: "status",
        width: 64,
        render: (v: Slot["status"]) => {
          const displayText = v === "연장" ? "정상" : v;
          return (
            <Tag color={colorMap[v]}>{renderLocalFindText(displayText)}</Tag>
          );
        },
      },
      {
        title: "생성자",
        dataIndex: "manager",
        width: 100,
        render: (_, rec) => {
          const id = rec.manager ?? rec.creator;
          if (!id) return "-";

          // 특정 ID에 대한 매핑
          if (id === 13) return <>{renderLocalFindText("adpa99")}</>;

          // creator인 경우 서버가 내려주는 username을 우선 사용
          if (!rec.manager) {
            return (
              <>
                {renderLocalFindText(rec.creator_username ?? getUsername(id))}
              </>
            );
          }
          // manager는 별도 username 필드가 없을 수 있어 기존 매핑/lookup 유지
          return <>{renderLocalFindText(getUsername(id))}</>;
        },
      },
      {
        title: "아이디",
        dataIndex: "user",
        width: 120,
        align: "center",
        render: (id: number, r) =>
          isEditing(r) ? (
            <Select
              style={{ width: "100%" }}
              showSearch
              optionFilterProp="label"
              optionLabelProp="label"
              value={getVal(r, "user") as number | undefined}
              onChange={(val) => updateDraft(r.slotId, { user: val })}
            >
              {sellers.map((s) => (
                <Select.Option
                  key={s.id}
                  value={s.id}
                  label={`${s.username} (${s.name})`}
                >
                  {s.username} ({s.name})
                </Select.Option>
              ))}
            </Select>
          ) : (
            renderLocalFindText(r.user_username ?? getUsername(id))
          ),
      },
      {
        title: "가격 비교 순위",
        dataIndex: "today_rank",
        width: 140,
        align: "center" as const,
        // complete면 today_rank(+new), 미완료면 yesterday_rank 우선, 없으면 상태 문구
        // 대기/실패 상태 문구도 클릭하면 이전 순위 이력을 확인할 수 있습니다.
        render: (_todayRank: number | null | undefined, record: Slot) => {
          const { label, showNew, color, clickable } = getRankCellView(record);
          const handleRankClick = () => {
            if (!clickable || record.is_optimistic) return;
            setRankModalSlotId(record.slotId);
            setRankModalMainKeyword(record.main_keyword ?? null);
            setRankModalVisible(true);
          };

          return (
            <RankCellContent
              label={label}
              showNew={showNew}
              color={color}
              onClick={clickable ? handleRankClick : undefined}
              renderLocalFindText={renderLocalFindText}
            />
          );
        },
      },
      {
        title: "남은일수",
        dataIndex: "remaining_days",
        width: 85,
        align: "center",
        render: (v: number) =>
          v === 0 ? (
            <div
              style={{
                backgroundColor: "#ffefef",
                fontWeight: "bold",
                color: "#d9534f",
                padding: "2px 6px",
                borderRadius: 4,
                textAlign: "center",
              }}
            >
              {renderLocalFindText(v)}
            </div>
          ) : (
            renderLocalFindText(v)
          ),
      },
      {
        title: "시작일",
        dataIndex: "start_date",
        width: 120,
        render: (v, r) =>
          isEditing(r) && canEditDates ? (
            <DatePicker
              style={{ width: "100%" }}
              value={(() => {
                const val = getVal(r, "start_date") as string | "";
                return val ? dayjs(val, dateFormat) : null;
              })()}
              disabledDate={(d) => {
                const end = getVal(r, "end_date") as string | "";
                return end ? d.isAfter(dayjs(end, dateFormat), "day") : false;
              }}
              onChange={(d) =>
                updateDraft(r.slotId, {
                  start_date: d ? d.format(dateFormat) : "",
                })
              }
              format={dateFormat}
            />
          ) : (
            renderLocalFindText(v)
          ),
      },
      {
        title: "종료일",
        dataIndex: "end_date",
        width: 120,
        render: (v, r) =>
          isEditing(r) && canEditDates ? (
            <DatePicker
              style={{ width: "100%" }}
              value={(() => {
                const val = getVal(r, "end_date") as string | "";
                return val ? dayjs(val, dateFormat) : null;
              })()}
              disabledDate={(d) => {
                const start = getVal(r, "start_date") as string | "";
                return start
                  ? d.isBefore(dayjs(start, dateFormat), "day")
                  : false;
              }}
              onChange={(d) =>
                updateDraft(r.slotId, {
                  end_date: d ? d.format(dateFormat) : "",
                })
              }
              format={dateFormat}
            />
          ) : (
            renderLocalFindText(v)
          ),
      },

      /* ───── 편집 가능 필드들 ───── */
      {
        title: "메인키워드",
        dataIndex: "main_keyword",
        render: (v, r) =>
          isEditing(r) ? (
            <SlotDraftTextInput
              slotId={r.slotId}
              field="main_keyword"
              initialValue={String(getVal(r, "main_keyword") ?? "")}
              onDraftChange={updateDraftSilent}
            />
          ) : (
            renderLocalFindText(v)
          ),
      },
      {
        title: "단일 MID",
        dataIndex: "mid_single",
        width: 140,
        render: (v, r) =>
          isEditing(r) ? (
            <SlotDraftTextInput
              slotId={r.slotId}
              field="mid_single"
              initialValue={String(getVal(r, "mid_single") ?? "")}
              onDraftChange={updateDraftSilent}
            />
          ) : (
            renderLocalFindText(v)
          ),
      },
      {
        title: "가격비교 MID",
        dataIndex: "mid_compare",
        width: 140,
        render: (v, r) =>
          isEditing(r) ? (
            <SlotDraftTextInput
              slotId={r.slotId}
              field="mid_compare"
              initialValue={String(getVal(r, "mid_compare") ?? "")}
              onDraftChange={updateDraftSilent}
            />
          ) : (
            renderLocalFindText(v)
          ),
      },
      {
        title: "상품 URL",
        dataIndex: "product_url",
        width: 110,
        render: (v, r) =>
          isEditing(r) ? (
            <SlotDraftTextInput
              slotId={r.slotId}
              field="product_url"
              initialValue={String(getVal(r, "product_url") ?? "")}
              onDraftChange={updateDraftSilent}
              validate={isValidUrl}
            />
          ) : v ? (
            <a href={v} target="_blank" rel="noreferrer">
              {renderLocalFindText("바로가기")}
            </a>
          ) : (
            "-"
          ),
      },
      {
        title: "가격비교 URL",
        dataIndex: "compare_url",
        width: 110,
        render: (v, r) =>
          isEditing(r) ? (
            <SlotDraftTextInput
              slotId={r.slotId}
              field="compare_url"
              initialValue={String(getVal(r, "compare_url") ?? "")}
              onDraftChange={updateDraftSilent}
              validate={isValidUrl}
            />
          ) : v ? (
            <a href={v} target="_blank" rel="noreferrer">
              {renderLocalFindText("바로가기")}
            </a>
          ) : (
            "-"
          ),
      },
      {
        title: "메모",
        dataIndex: "memo",
        render: (v, r) =>
          isEditing(r) ? (
            <SlotDraftTextInput
              slotId={r.slotId}
              field="memo"
              initialValue={String(getVal(r, "memo") ?? "")}
              onDraftChange={updateDraftSilent}
            />
          ) : (
            renderLocalFindText(v)
          ),
      },

      /* ───── 관리 버튼 ───── */
      {
        title: "수정",
        dataIndex: "manage",
        width: 150,
        fixed: "right",
        render: (_, r) => {
          if (r.is_optimistic) {
            return (
              <span style={{ color: "#8c8c8c", whiteSpace: "nowrap" }}>
                생성 중
              </span>
            );
          }

          const permissions = getSlotPermissions(r, currentUser, isRestricted);
          const isCreateRestricted = currentUser
            ? isSlotOperationRestricted(currentUser, "생성")
            : false;
          const canClone =
            !!onClone && currentUser?.grade !== "SELLER" && !isCreateRestricted;

          if (isEditing(r)) {
            return (
              <Button
                size="small"
                type="primary"
                loading={savingRows.has(r.slotId)}
                disabled={savingRows.has(r.slotId)}
                onClick={async () => {
                  if (savingRows.has(r.slotId)) return;
                  const dto = draftsRef.current[r.slotId] || {};
                  const urlInvalid =
                    (dto.product_url && !isValidUrl(dto.product_url)) ||
                    (dto.compare_url && !isValidUrl(dto.compare_url));
                  if (urlInvalid) {
                    message.error("올바른 URL 형식이 아닙니다.");
                    return;
                  }

                  try {
                    setSavingRows((prev) => {
                      const next = new Set(prev);
                      next.add(r.slotId);
                      return next;
                    });
                    // API 요청이 성공한 후에 UI 상태 변경
                    await onInlineUpdate(r.slotId, dto);
                    setEditingRows((s) => {
                      const n = new Set(s);
                      n.delete(r.slotId);
                      return n;
                    });
                    setCompletedRows((s) => new Set(s).add(r.slotId));
                    delete draftsRef.current[r.slotId];
                  } catch (error) {
                    // 에러 발생 시 UI 상태 변경하지 않음
                  } finally {
                    setSavingRows((prev) => {
                      const next = new Set(prev);
                      next.delete(r.slotId);
                      return next;
                    });
                  }
                }}
              >
                완료
              </Button>
            );
          }

          if (completedRows.has(r.slotId)) {
            return (
              <Button
                size="small"
                disabled={!permissions.canEdit}
                onClick={() => {
                  setCompletedRows((s) => {
                    const n = new Set(s);
                    n.delete(r.slotId);
                    return n;
                  });
                  setEditingRows((s) => new Set(s).add(r.slotId));
                }}
              >
                수정
              </Button>
            );
          }

          return (
            <Space size={4} wrap={false}>
              <Button
                size="small"
                disabled={!permissions.canEdit}
                onClick={() => setEditingRows((s) => new Set(s).add(r.slotId))}
              >
                수정
              </Button>
              <Button
                size="small"
                disabled={!canClone}
                onClick={() => onClone?.(r)}
              >
                복제
              </Button>
            </Space>
          );
        },
      },
    ],
    [
      currentUser,
      sellers,
      isRestricted,
      canEditDates,
      editingRows,
      completedRows,
      savingRows,
      onInlineUpdate,
      onClone,
      getUsername,
      isValidUrl,
      isEditing,
      getVal,
      updateDraft,
      updateDraftSilent,
      renderLocalFindText,
    ],
  );

  // 테이블 변경 핸들러
  const handleTableChange = useCallback(
    (pagination: any, filters: any, sorter: any) => {
      // 페이지 변경만 처리
      if (pagination.current !== undefined) {
        onPageChange?.(pagination.current, pagination.pageSize || 20);
      }
    },
    [onPageChange],
  );

  // 페이지네이션 설정
  const tablePagination = useMemo(
    () =>
      pagination
        ? {
            current: pagination.current_page || 1,
            pageSize: pagination.page_size || 20,
            total: pagination.total_count || 0,
            position: ["bottomCenter" as const],
            showSizeChanger: true,
            pageSizeOptions: [
              "20",
              "50",
              "100",
              "200",
              "300",
              "400",
              "500",
              "1000",
            ],
            showQuickJumper: false,
            showTotal: (total: number, range: [number, number]) =>
              `${range[0]}-${range[1]} / ${total.toLocaleString()}개 슬롯`,
            showLessItems: true,
            responsive: true,
          }
        : false,
    [pagination],
  );

  const rowSelection = useMemo(
    () => ({
      type: "checkbox" as const,
      columnWidth: 46,
      selectedRowKeys: selected,
      onChange: onSelectChange,
      getCheckboxProps: (record: Slot) => ({
        disabled: record.is_optimistic === true || record.slotId < 0,
      }),
    }),
    [selected, onSelectChange],
  );

  const tableScroll = useMemo(
    () =>
      useVirtual
        ? { x: VIRTUAL_SCROLL_X, y: VIRTUAL_SCROLL_Y }
        : { x: "max-content" as const },
    [useVirtual],
  );

  const rowClassName = useCallback(
    (record: Slot) =>
      record.is_optimistic
        ? "slot-table-optimistic-row"
        : normalizedLocalFindQuery && record.slotId === activeLocalFindSlotId
          ? "slot-table-local-find-active-row"
          : "",
    [activeLocalFindSlotId, normalizedLocalFindQuery],
  );

  const handleRow = useCallback(
    (record: Slot) =>
      record.slotId === activeLocalFindSlotId ? { tabIndex: -1 } : {},
    [activeLocalFindSlotId],
  );

  return (
    <>
      <div ref={tableWrapperRef}>
        <Table
          ref={tableRef as any}
          rowKey="slotId"
          dataSource={data}
          columns={columns}
          loading={loading}
          virtual={useVirtual}
          rowSelection={rowSelection}
          rowClassName={rowClassName}
          onRow={handleRow}
          className="slot-table-local-find"
          style={isMobile ? { width: "100%" } : { minWidth: 950 }}
          scroll={tableScroll}
          pagination={tablePagination}
          onChange={handleTableChange}
        />
      </div>

      <SlotRankModal
        visible={rankModalVisible}
        slotId={rankModalSlotId}
        mainKeyword={rankModalMainKeyword}
        onClose={() => {
          setRankModalVisible(false);
          setRankModalSlotId(null);
          setRankModalMainKeyword(null);
        }}
      />
    </>
  );
};

export default React.memo(SlotTable);
